import { differenceInDays, isToday } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';

import { getStripTime, useTranslation } from '@abg/kit-base';

import { useBookingWidgetContext } from '../../../organisms/BookingWidget/hooks';
import {
  DateTimePickerErrorEnum,
  DateTimePickerProperties,
  HandleCancel,
  HandleValueChange,
  InitialDraftStateT,
} from '../types';
import {
  calculateSelectedDays,
  checkTodayAsUnavailableDay,
  formatTimePickerSubtitleTime,
  getLocationTimeOptions,
  getSelectedDaySchedule,
  getSplittedLocationHolidays,
  getTimeOptions,
} from '../utils';

type useDateTimePickerControllerProps = Pick<
  DateTimePickerProperties,
  | 'onClose'
  | 'onDone'
  | 'uiTextData'
  | 'timePickerOptions'
  | 'pickUpOpeningHours'
  | 'pickupHolidays'
  | 'dropoffOpeningHours'
  | 'dropoffHolidays'
  | 'dropoff24hrsFlag'
  | 'initialDraftState'
  | 'timeFormat'
  | 'maxBookingPeriod'
>;

export const useDateTimePickerController = ({
  onClose,
  onDone,
  uiTextData,
  timePickerOptions,
  pickUpOpeningHours,
  pickupHolidays,
  dropoffOpeningHours,
  dropoffHolidays,
  dropoff24hrsFlag,
  initialDraftState,
  timeFormat,
  maxBookingPeriod,
}: useDateTimePickerControllerProps) => {
  const { model } = useBookingWidgetContext();
  const t = useTranslation(model.i18n);

  const [draftValues, setDraftValues] =
    useState<InitialDraftStateT>(initialDraftState);

  const { dates: { from, to } = {}, pickupTime, dropoffTime } = draftValues;
  const isSingleDaySelection =
    from && to && getStripTime(from) === getStripTime(to);

  useEffect(() => {
    setDraftValues(initialDraftState);
  }, [initialDraftState]);

  const [workingPickupHolidays, nonWorkingPickupHolidays] =
    getSplittedLocationHolidays(pickupHolidays);

  const [workingDropoffHolidays, nonWorkingDropoffHolidays] =
    getSplittedLocationHolidays(dropoffHolidays);

  const { timePickerDropoff24hSubTitle } = uiTextData;

  const timeOptionsForTodayRelatedToPickupLocation = getLocationTimeOptions({
    daySchedule: getSelectedDaySchedule({
      selectedDay: new Date(),
      locationOpeningHours: pickUpOpeningHours,
      locationWorkingHolidays: workingPickupHolidays,
    }),
    timePickerOptions,
  });
  const isPickupDayToday = isToday(draftValues.dates?.from as Date);

  const isPickupDayWithLocationScheduleSelected =
    pickUpOpeningHours?.length > 0 && draftValues.dates?.from !== undefined;

  const isDropoffDayWithLocationScheduleSelected =
    dropoffOpeningHours?.length > 0 && draftValues.dates?.to !== undefined;

  const todayAsUnavailableDay = checkTodayAsUnavailableDay({
    timeOptionsForToday: timePickerOptions,
    timeOptionsForTodayRelatedToPickupLocation,
    withSelectedPickupSchedule: pickUpOpeningHours?.length > 0,
  });

  const selectedPickupDaySchedule = getSelectedDaySchedule({
    selectedDay: draftValues.dates?.from,
    locationOpeningHours: pickUpOpeningHours,
    locationWorkingHolidays: workingPickupHolidays,
  });

  const selectedDropoffDaySchedule = getSelectedDaySchedule({
    selectedDay: draftValues.dates?.to,
    locationOpeningHours: dropoffOpeningHours,
    locationWorkingHolidays: workingDropoffHolidays,
  });

  const pickupSubtitle = isPickupDayWithLocationScheduleSelected
    ? t('bookingWidget.time.pickup.field.openhours.description', {
        time: formatTimePickerSubtitleTime(
          selectedPickupDaySchedule,
          timeFormat,
        ),
      })
    : '';

  const dropoffSubtitle = isDropoffDayWithLocationScheduleSelected
    ? dropoff24hrsFlag
      ? timePickerDropoff24hSubTitle
      : t('bookingWidget.time.dropoff.field.openhours.description', {
          time: formatTimePickerSubtitleTime(
            selectedDropoffDaySchedule,
            timeFormat,
          ),
        })
    : '';

  const pickupLocationTimeOptions = getTimeOptions({
    isToday: isPickupDayToday,
    withSchedule: isPickupDayWithLocationScheduleSelected,
    timeOptionsForToday: timePickerOptions,
    timeOptionsForTodayRelatedToPickupLocation,
    daySchedule: selectedPickupDaySchedule,
    timePickerOptions,
  });

  const dropoffLocationTimeOptions = getTimeOptions({
    isToday: false,
    withSchedule: isDropoffDayWithLocationScheduleSelected,
    timePickerOptions,
    daySchedule: selectedDropoffDaySchedule,
    is24hrs: dropoff24hrsFlag,
    singleDaySelectionPickupTime: isSingleDaySelection ? pickupTime : '',
  });

  const isSelectedPickupTimeAvailable = pickupLocationTimeOptions.some(
    (option) => option.label === draftValues.pickupTime,
  );

  const isSelectedDropoffTimeAvailable = dropoffLocationTimeOptions.some(
    (option) => option.label === draftValues.dropoffTime,
  );

  useEffect(() => {
    const shouldInitializePickupTime =
      !isSelectedPickupTimeAvailable && pickupLocationTimeOptions.length > 0;
    if (shouldInitializePickupTime) {
      setDraftValues((prevState) => ({
        ...prevState,
        pickupTime: pickupLocationTimeOptions.some(
          (option) => option.label === initialDraftState.pickupTime,
        )
          ? initialDraftState.pickupTime
          : (pickupLocationTimeOptions[0]?.label as string),
      }));
    }
  }, [
    isSelectedPickupTimeAvailable,
    pickupLocationTimeOptions,
    initialDraftState.pickupTime,
  ]);

  useEffect(() => {
    const shouldInitializeDropoffTime =
      !isSelectedDropoffTimeAvailable && dropoffLocationTimeOptions.length > 0;
    if (shouldInitializeDropoffTime) {
      setDraftValues((prevState) => ({
        ...prevState,
        dropoffTime: dropoffLocationTimeOptions.some(
          (option) => option.label === initialDraftState.dropoffTime,
        )
          ? initialDraftState.dropoffTime
          : (dropoffLocationTimeOptions?.at(-1)?.label as string),
      }));
    }
  }, [
    isSelectedDropoffTimeAvailable,
    dropoffLocationTimeOptions,
    initialDraftState.dropoffTime,
  ]);

  const handleValueChange: HandleValueChange = (field, value) => {
    setDraftValues((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const handleCancel: HandleCancel = (event_) => {
    if ((event_.target as HTMLElement).tagName.toLowerCase() === 'body') {
      return;
    }
    setDraftValues(initialDraftState);
    onClose();
  };

  const handleDone = () => {
    onDone(draftValues);
    onClose();
  };

  const pickUpLocationClosedDays =
    pickUpOpeningHours?.reduce((accumulator: number[], daySchedule) => {
      if (daySchedule.firstStartTime === -1) {
        accumulator.push(daySchedule.dayOfWeek);
      }
      return accumulator;
    }, []) || [];

  const dropoffLocationClosedDays =
    dropoffOpeningHours?.reduce((accumulator: number[], daySchedule) => {
      if (daySchedule.firstStartTime === -1) {
        accumulator.push(daySchedule.dayOfWeek);
      }
      return accumulator;
    }, []) || [];

  const daysSelected = useMemo(() => {
    if (!from || !to) {
      return 0;
    }

    const baseDaysDiff = differenceInDays(to, from);

    return calculateSelectedDays({
      baseDaysDiff,
      timeFormat,
      pickupTime,
      dropoffTime,
    });
  }, [from, to, pickupTime, dropoffTime, timeFormat]);

  const [showDateTimePickerError, setShowDateTimePickerError] = useState<
    DateTimePickerErrorEnum | boolean
  >(false);

  useEffect(() => {
    const showErrorMaxBookingPeriod = daysSelected > maxBookingPeriod;

    if (showErrorMaxBookingPeriod)
      setShowDateTimePickerError(DateTimePickerErrorEnum.maxBookingPeriod);
    else setShowDateTimePickerError(false);
  }, [daysSelected, maxBookingPeriod]);

  return {
    draftValues,
    nonWorkingPickupHolidays,
    nonWorkingDropoffHolidays,
    todayAsUnavailableDay,
    pickupSubtitle,
    dropoffSubtitle,
    pickupLocationTimeOptions,
    dropoffLocationTimeOptions,
    handleValueChange,
    handleCancel,
    handleDone,
    pickUpLocationClosedDays,
    dropoffLocationClosedDays,
    daysSelected,
    showDateTimePickerError,
  };
};
